import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CheckCircle, Scale, FileCheck, Users, BookOpen, FileText, Shield, Award, AlertTriangle, Info, HelpCircle, Briefcase } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'sobre', label: 'Sobre CERTILEX', icon: <Info className="w-4 h-4" /> },
  { id: 'servicios', label: 'Servicios', icon: <Briefcase className="w-4 h-4" /> },
  { id: 'legal', label: 'Fundamento Legal', icon: <Scale className="w-4 h-4" /> },
  { id: 'destinatarios', label: 'Destinatarios', icon: <Users className="w-4 h-4" /> },
  { id: 'soluciones', label: 'Soluciones', icon: <Award className="w-4 h-4" /> },
  { id: 'faq', label: 'Preguntas frecuentes', icon: <HelpCircle className="w-4 h-4" /> },
];

const Home = () => {
  const [activeSection, setActiveSection] = useState('sobre');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        toast({ title: "Mensaje enviado", description: "Gracias por contactarnos. Te responderemos pronto." });
        setFormData({ name: '', email: '', message: '' });
      } else {
        toast({ title: "Error", description: "No se pudo enviar el mensaje", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Error de conexión", variant: "destructive" });
    }
  };

  return (
    <div className="certilex-page">
      {/* Header */}
      <header className="cl-header" data-testid="main-header">
        <div className="cl-header-inner">
          <a href="/" className="cl-logo" data-testid="logo">CERTILEX<sup>&reg;</sup></a>
          <div className="cl-header-actions">
            <a href="/login" className="cl-btn-ghost" data-testid="login-btn">Iniciar Sesi&oacute;n</a>
            <a href="/register" className="cl-btn-gold" data-testid="register-btn">Registro Gratuito</a>
          </div>
        </div>
      </header>

      {/* TOP: Banner + Formulario */}
      <section className="cl-rd-top" data-testid="rd-banner">
        <div className="cl-rd-top-grid">
          <div className="cl-rd-top-text">
            <div className="cl-rd-top-badge"><AlertTriangle className="w-4 h-4" /><span>Especial &mdash; Urgente &mdash; Entrada en vigor: 1 julio 2026</span></div>
            <h2 className="cl-rd-top-title">Protege tu ejercicio profesional</h2>
            <p className="cl-rd-top-sub">Real Decreto 1277/2003 &mdash; Modificaci&oacute;n 2026: lo que todo cirujano est&eacute;tico debe saber</p>
            <a href="/cirugia-estetica" className="cl-rd-top-btn" data-testid="rd-banner-btn">Acceder a la informaci&oacute;n completa</a>
          </div>
          <form onSubmit={handleSubmit} className="cl-rd-top-form" data-testid="contact-form">
            <h3 className="cl-rd-form-title">Cont&aacute;ctenos</h3>
            <Input type="text" name="name" placeholder="Nombre" required value={formData.name} onChange={handleInputChange} className="cl-rd-input" data-testid="contact-name" />
            <Input type="email" name="email" placeholder="Correo electr&oacute;nico" required value={formData.email} onChange={handleInputChange} className="cl-rd-input" data-testid="contact-email" />
            <Textarea name="message" placeholder="Mensaje" required rows={3} value={formData.message} onChange={handleInputChange} className="cl-rd-input resize-none" data-testid="contact-message" />
            <Button type="submit" className="cl-rd-form-btn" data-testid="contact-submit">Enviar Mensaje</Button>
          </form>
        </div>
      </section>

      {/* Section Navigation */}
      <nav className="cl-tabs" data-testid="section-nav">
        <div className="cl-tabs-inner">
          {SECTIONS.map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`cl-tab${activeSection === id ? ' cl-tab--active' : ''}`}
              data-testid={`tab-${id}`}
            >
              {icon}<span>{label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Content Area */}
      <main className="cl-content" data-testid="content-area">

        {/* === SOBRE === */}
        {activeSection === 'sobre' && (
          <div className="cl-panel" data-testid="panel-sobre">
            <div className="cl-panel-grid">
              <div>
                <h2 className="cl-panel-title">Certificaci&oacute;n Jur&iacute;dica de Competencia Profesional</h2>
                <div className="cl-panel-quote">
                  <p>El t&iacute;tulo de especialista presupone competencia. La certificaci&oacute;n jur&iacute;dica la prueba.</p>
                </div>
                <p className="cl-panel-text">
                  CERTILEX es una plataforma jur&iacute;dica que tramita la certificaci&oacute;n de la competencia profesional mediante instrumentos con plena validez legal. No sustituimos t&iacute;tulos oficiales: validamos la experiencia y formaci&oacute;n demostrada cuando el t&iacute;tulo no existe, no refleja la realidad o no ha sido convalidado.
                </p>
                <p className="cl-panel-text">
                  El concepto de &ldquo;competencia profesional&rdquo; que la Organizaci&oacute;n M&eacute;dica Colegial (OMC) define como formaci&oacute;n + experiencia queda as&iacute; documentado y validado jur&iacute;dicamente ante pacientes, clientes, sociedades cient&iacute;ficas, compa&ntilde;&iacute;as aseguradoras, cl&iacute;nicas, etc.
                </p>
              </div>
              <div className="cl-panel-img-wrap">
                <img src="https://images.unsplash.com/photo-1589578527966-fdac0f44566c?w=600&q=80" alt="Justicia" className="cl-panel-img" />
              </div>
            </div>

            <div className="cl-panel-desc">
              <h3 className="cl-panel-subtitle">Certificaciones Legales para Profesionales Sanitarios</h3>
              <p className="cl-panel-text">
                CERTILEX&reg; es una plataforma jur&iacute;dica especializada en la certificaci&oacute;n y validaci&oacute;n de la capacitaci&oacute;n profesional, especialmente en el &aacute;mbito sanitario. Facilitamos la emisi&oacute;n de actas notariales electr&oacute;nicas, certificados de notoriedad y otros documentos con plena validez legal, respaldando la trayectoria y competencias de m&eacute;dicos y profesionales ante sus respectivas Organizaciones Colegiales, incorpor&aacute;ndolos a su expediente, compa&ntilde;&iacute;as aseguradoras, cl&iacute;nicas y hospitales.
              </p>
              <p className="cl-panel-text">Todo ello online, sin necesidad de desplazamientos.</p>
            </div>
          </div>
        )}

        {/* === SERVICIOS === */}
        {activeSection === 'servicios' && (
          <div className="cl-panel cl-panel--dark" data-testid="panel-servicios">
            <h2 className="cl-panel-title cl-panel-title--light">&iquest;Qu&eacute; ofrece CERTILEX?</h2>
            <p className="cl-panel-subtitle-text cl-panel-subtitle-text--light">Estructuramos nuestro servicio en dos v&iacute;as complementarias</p>
            <div className="cl-panel-cards">
              <div className="cl-panel-card cl-panel-card--dark">
                <div className="cl-panel-card-icon"><FileText className="w-7 h-7" /></div>
                <h3>Declaraci&oacute;n Responsable Validada</h3>
                <ul>
                  <li><CheckCircle className="w-4 h-4" /><span>El profesional presenta su formaci&oacute;n y experiencia.</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>CERTILEX gestiona, revisa y estructura el expediente.</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>Se genera una declaraci&oacute;n responsable formal, lista para ser incorporada al expediente colegial o elevada a documento p&uacute;blico notarial.</span></li>
                </ul>
              </div>
              <div className="cl-panel-card cl-panel-card--dark">
                <div className="cl-panel-card-icon"><Scale className="w-7 h-7" /></div>
                <h3>Acta Notarial de Notoriedad Profesional</h3>
                <ul>
                  <li><CheckCircle className="w-4 h-4" /><span>Cuando hay documentaci&oacute;n s&oacute;lida o testigos, CERTILEX gestiona el expediente ante nuestros notarios colaboradores.</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>El notario, de forma independiente, eval&uacute;a y emite un acta notarial con plena validez jur&iacute;dica y legal.</span></li>
                  <li><CheckCircle className="w-4 h-4" /><span>La certificaci&oacute;n se incorpora posteriormente al expediente colegial en el Colegio Oficial de M&eacute;dicos correspondiente.</span></li>
                </ul>
              </div>
            </div>
            <div className="cl-panel-note">
              <Shield className="w-5 h-5 flex-shrink-0" />
              <p>CERTILEX no emite certificaciones: gestiona y estructura expedientes. Las certificaciones son emitidas por nuestros notarios colaboradores con plena validez jur&iacute;dica y legal.</p>
            </div>
          </div>
        )}

        {/* === LEGAL === */}
        {activeSection === 'legal' && (
          <div className="cl-panel" data-testid="panel-legal">
            <div className="cl-panel-center">
              <BookOpen className="w-10 h-10 cl-text-gold" />
              <h2 className="cl-panel-title">Fundamento legal</h2>
              <p className="cl-panel-text">
                Las actas de notoriedad se regulan por el art&iacute;culo 209 del Reglamento Notarial. Permiten al notario dar fe de hechos notorios como el ejercicio profesional continuado y reconocido en un &aacute;rea determinada.
              </p>
              <p className="cl-panel-text">
                Las declaraciones responsables tienen valor jur&iacute;dico como prueba documental de la experiencia y la competencia, especialmente cuando est&aacute;n bien fundamentadas y estructuradas.
              </p>
            </div>
          </div>
        )}

        {/* === DESTINATARIOS === */}
        {activeSection === 'destinatarios' && (
          <div className="cl-panel" data-testid="panel-destinatarios">
            <div className="cl-panel-center">
              <Users className="w-10 h-10 cl-text-gold" />
              <h2 className="cl-panel-title">&iquest;A qui&eacute;n va dirigido?</h2>
              <ul className="cl-panel-list">
                {[
                  'M\u00e9dicos sin especialidad oficial con experiencia s\u00f3lida.',
                  'M\u00e9dicos especialistas extranjeros con el t\u00edtulo b\u00e1sico convalidado en Espa\u00f1a.',
                  'Especialistas titulados que desean documentar detalladamente su trayectoria.',
                  'Otros profesionales sanitarios como nutricionistas, psic\u00f3logos cl\u00ednicos, home\u00f3patas, acupuntores, quiropr\u00e1cticos, oste\u00f3patas, etc.'
                ].map((item, i) => (
                  <li key={i}><CheckCircle className="w-5 h-5 cl-text-gold flex-shrink-0" /><span>{item}</span></li>
                ))}
              </ul>
              <div className="cl-panel-info">
                <p>CERTILEX solo tramita expedientes de titulados con su t&iacute;tulo b&aacute;sico convalidado en Espa&ntilde;a. La formaci&oacute;n o experiencia puede provenir del extranjero, y el solicitante puede residir fuera de Espa&ntilde;a. La tramitaci&oacute;n es 100% electr&oacute;nica.</p>
              </div>
            </div>
          </div>
        )}

        {/* === SOLUCIONES === */}
        {activeSection === 'soluciones' && (
          <div className="cl-panel" data-testid="panel-soluciones">
            <h2 className="cl-panel-title">Soluciones Legales para Profesionales del Sector Salud</h2>
            <div className="cl-panel-solutions">
              <div className="cl-panel-solution">
                <FileCheck className="w-10 h-10 cl-text-gold" />
                <h3>Certificaci&oacute;n de Competencias M&eacute;dicas</h3>
                <p>Asegure su validez profesional: certificaciones con plena validez legal.</p>
              </div>
              <div className="cl-panel-solution">
                <Scale className="w-10 h-10 cl-text-gold" />
                <h3>Asesoramiento Legal Personalizado</h3>
                <p>Obtenga orientaci&oacute;n experta para cumplir con todas las normativas legales del &aacute;mbito sanitario.</p>
              </div>
              <div className="cl-panel-solution">
                <Award className="w-10 h-10 cl-text-gold" />
                <h3>Tramitaci&oacute;n 100% Online</h3>
                <p>Todo el proceso de certificaci&oacute;n se realiza de forma electr&oacute;nica, sin necesidad de desplazamientos.</p>
              </div>
            </div>
          </div>
        )}

        {/* === FAQ === */}
        {activeSection === 'faq' && (
          <div className="cl-panel" data-testid="panel-faq">
            <h2 className="cl-panel-title">Preguntas frecuentes</h2>
            <div className="cl-panel-center">
              <Accordion type="single" collapsible className="cl-faq-list" style={{width:'100%'}}>
                <AccordionItem value="item-1" className="cl-faq-item">
                  <AccordionTrigger className="cl-faq-trigger">&iquest;CERTILEX emite certificados?</AccordionTrigger>
                  <AccordionContent className="cl-faq-content">
                    No. CERTILEX gestiona expedientes. Las certificaciones son emitidas con plena validez jur&iacute;dica y legal por nuestros notarios colaboradores. Posteriormente, estas certificaciones se incorporan al expediente colegial en el Colegio Oficial de M&eacute;dicos correspondiente.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2" className="cl-faq-item">
                  <AccordionTrigger className="cl-faq-trigger">&iquest;Las certificaciones se incorporan al expediente colegial?</AccordionTrigger>
                  <AccordionContent className="cl-faq-content">
                    S&iacute;. Las certificaciones emitidas por nuestros notarios colaboradores se incorporan posteriormente al expediente colegial en el Colegio Oficial de M&eacute;dicos correspondiente. CERTILEX gestiona todo el proceso y fomenta activamente esta incorporaci&oacute;n.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3" className="cl-faq-item">
                  <AccordionTrigger className="cl-faq-trigger">&iquest;Se puede usar desde el extranjero?</AccordionTrigger>
                  <AccordionContent className="cl-faq-content">
                    S&iacute;, siempre que el t&iacute;tulo b&aacute;sico est&eacute; convalidado en Espa&ntilde;a. Toda la tramitaci&oacute;n se realiza por v&iacute;a electr&oacute;nica.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="cl-footer" data-testid="footer">
        <div className="cl-container">
          <div className="cl-footer-main">
            <div className="cl-footer-brand">
              <h3 className="cl-footer-logo">CERTILEX<sup>&reg;</sup></h3>
              <p className="cl-footer-desc">Gesti&oacute;n Notarial y Registro Colegial de Competencias</p>
            </div>
            <div className="cl-footer-dimps">
              <p>
                El proyecto Certilex&reg; parte de nuestra American Limited Liability Company <strong>DIMPS</strong>. Contamos con un equipo de profesionales de primer nivel en la abogac&iacute;a, notar&iacute;a y asuntos p&uacute;blicos y regulatorios. Nuestro objetivo: que la trayectoria profesional de nuestros clientes se proyecte al m&aacute;ximo nivel de sus competencias.
              </p>
            </div>
          </div>
          <div className="cl-footer-bottom">
            <p>Contacto: <a href="mailto:info@certilex.es">info@certilex.es</a></p>
            <p>&copy; {new Date().getFullYear()} CERTILEX. Todos los derechos reservados.</p>
            <p className="cl-footer-credit">Dise&ntilde;o: <strong>DIMPS</strong> &mdash; Servicios y Marketing Digitales</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
